import { Transition, Variants } from "framer-motion";

/**
 * Shared motion vocabulary for the native onboarding flow — mirrors
 * Chat/chatMotion.ts so the app keeps one physics signature. Everything is
 * transform/opacity/pathLength only (composited on the WebView) and every
 * variant is bypassed by framer-motion's useReducedMotion() at the call site.
 */

/** The house spring (same as chatMotion's composerMorph). */
export const onboardingSpring: Transition = {
  type: "spring",
  stiffness: 360,
  damping: 32,
};

/** The house ease curve (same as chatMotion's heroRise). */
export const onboardingEase = [0.22, 1, 0.36, 1] as const;

/** Direction-aware step transition: forward enters from the right and exits
    left; back is mirrored. `custom` is +1 (forward) or -1 (back). */
export const stepVariants: Variants = {
  enter: (direction: number) => ({
    x: direction >= 0 ? 32 : -32,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.3, ease: onboardingEase },
  },
  exit: (direction: number) => ({
    x: direction >= 0 ? -24 : 24,
    opacity: 0,
    transition: { duration: 0.18, ease: "easeIn" },
  }),
};

/** Parent for staggered lists (option rows, plan cards). */
export const staggerParent: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
};

/** Children of staggerParent: rise in with a fade. */
export const staggerChild: Variants = {
  hidden: { y: 12, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.3, ease: onboardingEase } },
};

/** Slower cascade for the reveal's plan cards. */
export const cascadeParent: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};

export const cascadeChild: Variants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: onboardingSpring },
};

/** One-shot rise for headers/heroes; delay staggers siblings. */
export const riseIn = (delay = 0): Variants => ({
  hidden: { y: 14, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: onboardingEase, delay },
  },
});
